import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'
import EmblaCarousel from '../../../vendor/embla/embla-carousel.esm.js'
import EmblaAutoScroll from '../../../vendor/embla/embla-carousel-auto-scroll.esm.js'
import { BuilderContext } from '../BuilderContextInstance'

/**
 * Carrousel générique, entièrement piloté depuis le YAML.
 *
 * POURQUOI UNE PRIMITIVE (échappatoire config-first, justifiée) : la position de
 * défilement, la largeur mesurée du conteneur, l'inertie du glisser et le minuteur du
 * défilement automatique sont de l'état DOM — illisible depuis le store, donc hors de
 * portée d'un Switch ou d'un `setData`. Elle remplace les cinq rails `overflow-x-auto
 * snap-x` copiés-collés (DropteamSlider, EmeryaHeroSlider, BgreenArticlesCarousel,
 * EmeryaGallery, AcademieLesImmortels), qui n'avaient ni flèches, ni points, ni pause.
 * Aucun markup de slide n'est décidé ici : tout reste en YAML.
 *
 * MOTEUR : le cœur d'Embla, vendoré dans `src/vendor/embla/` (cf. son README). Retenu
 * parce qu'il ne rend AUCUN markup (le DS Cosmos et l'accessibilité restent à nous),
 * qu'il ne clone ni ne réordonne les nœuds — sa boucle translate les slides existantes,
 * donc l'édition inline du builder n'est jamais dupliquée — et qu'il défile vers la
 * slide qui reçoit le focus (`watchFocus`).
 *
 * DEUX SOURCES, au choix :
 *   (A) `content:` — la primitive est le rail, on lui passe le Switch existant.
 *   (B) `items:` + `slideTemplate:` — elle itère elle-même, en rendant chaque slide au
 *       VRAI chemin du store (comme FilteredGallery), ce qui préserve `~.champ`,
 *       `editable:` et les rails d'édition à l'intérieur du carrousel. Marche aussi bien
 *       sur une liste statique que sur les `contents` d'une requête Elasticsearch.
 *
 * YAML (les valeurs montrées sont les valeurs par défaut) :
 *   - type: Carousel
 *     ariaLabel: "Nos réalisations"   # REQUIS — nom accessible de la région
 *     items: ~~.…data.slides          # (B) ; sinon `content:` (A)
 *     slideTemplate: slideTpl
 *     perView: null                   # nombre, ou table de seuils {"0": 1, "640": 2}
 *     gap: null                       # ex. "1rem"
 *     align: start                    # start | center | end
 *     axis: x                         # x | y — y : rail VERTICAL (au YAML de donner une
 *                                     #   hauteur au viewport et `flex-col` au rail)
 *     step: page                      # page (= perView slides) | 1
 *     disableBelow: 0                 # px : en dessous, le carrousel se désactive
 *     loop: false
 *     drag: true
 *     dragFree: false
 *     duration: 25
 *     autoplay: 0                     # ms entre deux diapositives ; défilement PAR À-COUPS
 *     autoScroll: 0                   # vitesse ; défilement CONTINU (exclut `autoplay`)
 *     repeat: 1                       # (B) rend la liste N fois — copies décoratives
 *                                     #   (aria-hidden, inert), jamais en édition : une
 *                                     #   bande de logos trop courte pour l'écran ne boucle
 *                                     #   pas (Embla exige un contenu plus large que la vue)
 *     arrows: false                   # true|"inside" | "outside" | "top"
 *     dots: false
 *     dotLabel: null                  # (B) champ de la diapositive AFFICHÉ dans sa pastille
 *                                     #   (ex. `title`) : la ligne entière devient le bouton,
 *                                     #   et son nom accessible est ce texte visible
 *     counter: false
 *     railTag: ul                     # slideTag: li (mode B)
 *     rootClass / viewportClass / railClass / slideClass / controlsClass
 *     arrowClass / dotClass / dotActiveClass / counterClass
 *     labels: { previous, next, goTo, pause, play, counter }
 *
 * RÉTROCOMPATIBILITÉ : sans `perView`, la primitive ne pose AUCUNE largeur — les classes
 * du YAML (`shrink-0 w-[85%] @5xl:w-[1170px]`) gardent la main. Un bloc existant migre
 * donc en remplaçant son `<ul>` par ce nœud, au pixel près.
 *
 * Enregistrée dans `builderPlugins.js` ET `App.jsx` (les deux collections sont distinctes).
 */

const LABELS = {
  previous: 'Diapositive précédente',
  next: 'Diapositive suivante',
  goTo: 'Aller à la diapositive {n}',
  pause: 'Mettre en pause le défilement',
  play: 'Reprendre le défilement',
  counter: '{i} / {n}',
}

/** Remplace les jetons `{n}` / `{i}` d'un libellé. */
const fill = (tpl, values) =>
  String(tpl).replace(/\{(\w+)\}/g, (m, k) => (k in values ? String(values[k]) : m))

/**
 * Résout `perView` pour une largeur donnée. Un nombre est constant ; une table
 * `{"0": 1, "640": 2}` se lit comme des seuils en pixels — le plus grand seuil atteint
 * gagne. Volontairement PAS l'option `breakpoints` d'Embla : celle-ci repose sur des
 * media queries (largeur du navigateur), alors que le canevas du builder simule un
 * appareil par sa propre largeur — la prévisualisation responsive mentirait.
 */
const resolvePerView = (perView, width) => {
  if (typeof perView === 'number' && perView > 0) return perView
  if (!perView || typeof perView !== 'object') return null
  const steps = Object.entries(perView)
    .map(([bp, n]) => [Number(bp), Number(n)])
    .filter(([bp, n]) => Number.isFinite(bp) && Number.isFinite(n) && n > 0)
    .sort((a, b) => a[0] - b[0])
  if (!steps.length) return null
  let value = steps[0][1]
  for (const [bp, n] of steps) if (width >= bp) value = n
  return value
}

export const Carousel = ({ props, path, datafield, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const builderCtx = useContext(BuilderContext)
  const resolve = useCallback(
    (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext }),
    [globalDataContext, templateContext],
  )

  const railId = useId()
  const viewportRef = useRef(null)
  const emblaRef = useRef(null)

  // ── Props ────────────────────────────────────────────────────────────────
  const ariaLabel = resolve(props.ariaLabel)
  const slideTemplate = resolve(props.slideTemplate)
  const perViewProp = resolve(props.perView)
  const gap = resolve(props.gap)
  const align = resolve(props.align) || 'start'
  const axis = resolve(props.axis) === 'y' ? 'y' : 'x'
  const step = resolve(props.step) ?? 'page'
  const disableBelow = Number(resolve(props.disableBelow)) || 0
  const loop = resolve(props.loop) === true
  const drag = resolve(props.drag) !== false
  const dragFree = resolve(props.dragFree) === true
  const duration = Number(resolve(props.duration)) || 25
  const autoplayMs = Number(resolve(props.autoplay)) || 0
  const autoScrollSpeed = Number(resolve(props.autoScroll)) || 0
  const repeat = Math.max(1, Math.floor(Number(resolve(props.repeat)) || 1))
  const arrows = resolve(props.arrows)
  const dots = resolve(props.dots) === true
  const counter = resolve(props.counter) === true
  const dotLabel = resolve(props.dotLabel) || null
  const RailTag = resolve(props.railTag) || 'ul'
  const SlideTag = resolve(props.slideTag) || 'li'
  const labels = { ...LABELS, ...(resolve(props.labels) || {}) }
  const cls = (v, fallback) => (typeof v === 'string' ? v : fallback)

  // Le mode édition du builder change trois comportements : rien ne doit bouger sous une
  // saisie en cours (le diff de l'édition inline se ferait sur un DOM déjà muté), et le
  // glisser d'Embla volerait les `pointerdown` du glisser-déposer de lignes.
  const isEditMode = !!builderCtx?.isEditMode

  // ── Source des slides ────────────────────────────────────────────────────
  const hasItems = props.items != null && slideTemplate != null
  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: hasItems ? props.items : undefined,
    defaultValue: [],
    globalDataContext,
    templateContext,
  })
  const items = hasItems && Array.isArray(formData) ? formData : []

  // ── Mesures ──────────────────────────────────────────────────────────────
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const node = viewportRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => setWidth((prev) => (prev === node.clientWidth ? prev : node.clientWidth)))
    ro.observe(node)
    setWidth(node.clientWidth)
    return () => ro.disconnect()
  }, [])

  const perView = resolvePerView(perViewProp, width)

  // ── Moteur ───────────────────────────────────────────────────────────────
  const options = useMemo(
    () => ({
      align,
      axis,
      loop: loop && !isEditMode,
      slidesToScroll: step === 'page' && perView ? perView : 1,
      duration: isEditMode ? 0 : duration,
      dragFree: dragFree && !isEditMode,
      watchDrag: drag && !isEditMode,
      containScroll: 'trimSnaps',
      // Sous `disableBelow`, Embla rend la main : le rail redevient une simple rangée
      // (au YAML de prévoir un `flex-wrap` ou un défilement natif s'il en veut un).
      active: !(disableBelow > 0 && width > 0 && width < disableBelow),
    }),
    [align, axis, loop, isEditMode, step, perView, duration, dragFree, drag, disableBelow, width],
  )
  const optionsRef = useRef(options)
  optionsRef.current = options

  const [state, setState] = useState({ snaps: 0, index: 0, canPrev: false, canNext: false })

  // Déclaré ICI (et non avec les autres états de mouvement, plus bas) parce que le
  // greffon de défilement continu se pose à la CRÉATION du moteur : il faut savoir dès
  // cet effet si l'utilisateur a demandé « moins d'animations ».
  const [reducedMotion, setReducedMotion] = useState(false)

  // Le greffon n'est monté que s'il doit tourner : `stopOnMouseEnter`/`stopOnFocusIn` sont
  // laissés à FALSE et le survol/focus est piloté par l'effet de mouvement plus bas —
  // sinon le greffon relancerait le défilement en quittant le bloc, y compris après un
  // appui sur Pause (WCAG 2.2.2 exige que l'arrêt demandé tienne).
  const autoScrollWanted = autoScrollSpeed > 0 && !isEditMode && !reducedMotion
  useEffect(() => {
    const node = viewportRef.current
    if (!node) return
    const plugins = autoScrollWanted
      ? [EmblaAutoScroll({
          speed: autoScrollSpeed,
          startDelay: 0,
          playOnInit: true,
          stopOnInteraction: false,
          stopOnMouseEnter: false,
          stopOnFocusIn: false,
        })]
      : []
    const embla = EmblaCarousel(node, optionsRef.current, plugins)
    emblaRef.current = embla
    const sync = () =>
      setState({
        snaps: embla.scrollSnapList().length,
        index: embla.selectedScrollSnap(),
        canPrev: embla.canScrollPrev(),
        canNext: embla.canScrollNext(),
      })
    embla.on('select', sync).on('reInit', sync)
    sync()
    return () => {
      embla.destroy()
      emblaRef.current = null
    }
    // Le jeu de greffons ne se change pas à chaud : basculer en édition (ou activer
    // « moins d'animations ») recrée l'instance.
  }, [autoScrollWanted, autoScrollSpeed])

  // Les options changent (largeur mesurée, passage en édition) → réinitialiser sans
  // détruire l'instance, pour ne pas perdre la position.
  const optionsKey = JSON.stringify(options)
  useEffect(() => {
    emblaRef.current?.reInit(optionsRef.current)
  }, [optionsKey])

  // Une slide ajoutée/retirée par le rail d'édition doit re-mesurer le carrousel.
  const slideCount = hasItems ? items.length : null
  useEffect(() => {
    if (slideCount != null) emblaRef.current?.reInit(optionsRef.current)
  }, [slideCount])

  // ── Défilement automatique ───────────────────────────────────────────────
  const [paused, setPaused] = useState(false)   // arrêt explicite, par le bouton
  const [hovered, setHovered] = useState(false) // survol ou focus dans le carrousel
  const [hidden, setHidden] = useState(false)   // onglet en arrière-plan
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const apply = () => setReducedMotion(mq.matches)
    apply()
    mq.addEventListener?.('change', apply)
    return () => mq.removeEventListener?.('change', apply)
  }, [])

  // Un carrousel qui défile hors écran consomme et désoriente au retour d'onglet.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const apply = () => setHidden(document.visibilityState === 'hidden')
    apply()
    document.addEventListener('visibilitychange', apply)
    return () => document.removeEventListener('visibilitychange', apply)
  }, [])

  // Hors écran, même règle : un défilement CONTINU repeint à chaque image, et ce coût
  // (recalcul de style de toute la page) courait tant que la page restait ouverte —
  // TBT et Speed Index d'une page publique ruinés par une bande de logos invisible.
  const [offscreen, setOffscreen] = useState(false)
  useEffect(() => {
    const el = viewportRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(([entry]) => setOffscreen(!entry.isIntersecting))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const autoplayOn = autoplayMs > 0 && !isEditMode && !reducedMotion && state.snaps > 1
  useEffect(() => {
    if (!autoplayOn || paused || hovered || hidden || offscreen) return
    const id = setInterval(() => {
      const embla = emblaRef.current
      if (!embla) return
      if (embla.canScrollNext()) embla.scrollNext()
      else embla.scrollTo(0)
    }, autoplayMs)
    return () => clearInterval(id)
  }, [autoplayOn, paused, hovered, hidden, offscreen, autoplayMs])

  // Défilement CONTINU : mêmes règles d'arrêt que le mode par à-coups (bouton Pause,
  // survol/focus, onglet caché, hors écran), appliquées au greffon. `optionsKey` est dans les
  // dépendances parce qu'un `reInit` (largeur mesurée) relance le greffon d'office :
  // sans ce réapplique, un carrousel mis en pause repartirait au premier redimensionnement.
  const autoScrollOn = autoScrollWanted && state.snaps > 1
  useEffect(() => {
    const plugin = emblaRef.current?.plugins?.()?.autoScroll
    if (!plugin) return
    if (paused || hovered || hidden || offscreen) plugin.stop()
    else if (!plugin.isPlaying()) plugin.play(0)
  }, [autoScrollOn, paused, hovered, hidden, offscreen, optionsKey])

  // Libellé visible de la pastille i : le champ `dotLabel` de SA diapositive.
  const dotText = (i) => {
    if (!dotLabel || !items.length) return ''
    const v = items[i % items.length]?.[dotLabel]
    return v == null ? '' : String(v)
  }

  // ── Rendu ────────────────────────────────────────────────────────────────
  const slides = hasItems
    ? items.map((item, index) => (
        <SlideTag key={index} className={cls(props.slideClass, 'min-w-0')}>
          <View
            props={{ load: slideTemplate }}
            path={`${formDataPath}.${index}`}
            datafield={index}
            currentData={item}
          />
        </SlideTag>
      )).concat(
        // Copies DÉCORATIVES (`repeat`) : hors du lecteur d'écran et de la tabulation,
        // et absentes en édition, où chaque slide doit rester celle de la donnée.
        isEditMode ? [] : Array.from({ length: repeat - 1 }, (_, copy) => items.map((item, index) => (
          <SlideTag key={`r${copy}-${index}`} className={cls(props.slideClass, 'min-w-0')} aria-hidden="true" inert>
            <View
              props={{ load: slideTemplate }}
              path={`${formDataPath}.${index}`}
              datafield={index}
              currentData={item}
            />
          </SlideTag>
        ))).flat(),
      )
    : props.content != null && (
        <View props={props.content} path={`${path}.content`} datafield="content" currentData={currentData?.content} />
      )

  // Largeur des slides : posée en variable CSS et appliquée par la recette
  // `.carousel-rail[data-per-view] > *`. Sans `perView`, l'attribut est absent et la
  // recette ne s'applique pas — les classes de largeur du YAML restent souveraines.
  const railStyle = {}
  if (gap) railStyle['--carousel-gap'] = gap
  if (perView) {
    railStyle['--carousel-per-view'] = perView
    railStyle['--carousel-slide-basis'] =
      gap && perView > 1
        ? `calc((100% - ${perView - 1} * ${gap}) / ${perView})`
        : `${100 / perView}%`
  }

  // Des contrôles sans rien à faire défiler sont un piège : on ne les rend pas.
  const hasControls = state.snaps > 1
  const arrowsWhere = arrows === true ? 'inside' : typeof arrows === 'string' ? arrows : null
  const showArrows = hasControls && !!arrowsWhere
  const motionOn = autoplayOn || autoScrollOn
  const showBar = hasControls && (dots || counter || motionOn || (showArrows && arrowsWhere !== 'inside'))

  const arrowButtons = showArrows && (
    <>
      <button
        type="button"
        className={cls(props.arrowClass, 'carousel-arrow')}
        data-direction="previous"
        aria-label={labels.previous}
        aria-controls={railId}
        disabled={!state.canPrev}
        onClick={() => emblaRef.current?.scrollPrev()}
      >
        <ChevronLeft size={20} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={cls(props.arrowClass, 'carousel-arrow')}
        data-direction="next"
        aria-label={labels.next}
        aria-controls={railId}
        disabled={!state.canNext}
        onClick={() => emblaRef.current?.scrollNext()}
      >
        <ChevronRight size={20} aria-hidden="true" />
      </button>
    </>
  )

  const bar = showBar && (
    <div className={cls(props.controlsClass, 'carousel-controls')}>
      {arrowsWhere !== 'inside' && arrowButtons}
      {dots && (
        <div className="carousel-dots">
          {Array.from({ length: state.snaps }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`${cls(props.dotClass, 'carousel-dot')} ${i === state.index ? cls(props.dotActiveClass, 'is-active') : ''}`}
              aria-label={dotText(i) || fill(labels.goTo, { n: i + 1 })}
              aria-current={i === state.index ? 'true' : undefined}
              aria-controls={railId}
              onClick={() => emblaRef.current?.scrollTo(i)}
            >
              {dotText(i) && <span className="carousel-dot-label">{dotText(i)}</span>}
            </button>
          ))}
        </div>
      )}
      {counter && (
        <p className={cls(props.counterClass, 'carousel-counter')}>
          {fill(labels.counter, { i: state.index + 1, n: state.snaps })}
        </p>
      )}
      {/* WCAG 2.2.2 « Pause, Stop, Hide » : tout mouvement automatique doit pouvoir
          être arrêté par l'utilisateur — le bouton n'est donc pas optionnel. */}
      {motionOn && (
        <button
          type="button"
          className="carousel-pause"
          aria-label={paused ? labels.play : labels.pause}
          onClick={() => setPaused((p) => !p)}
        >
          {paused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
        </button>
      )}
    </div>
  )

  return (
    <ActionDependant {...props}>
      <div
        className={`${cls(props.rootClass, '')} ${showArrows && arrowsWhere === 'inside' ? 'relative' : ''}`}
        data-carousel-arrows={arrowsWhere || undefined}
        role="group"
        aria-roledescription="carousel"
        aria-label={ariaLabel}
        // Le survol et le focus suspendent le défilement : lire ou cliquer une slide ne
        // doit jamais être interrompu par le carrousel lui-même.
        onMouseEnter={motionOn ? () => setHovered(true) : undefined}
        onMouseLeave={motionOn ? () => setHovered(false) : undefined}
        onFocus={motionOn ? () => setHovered(true) : undefined}
        onBlur={motionOn ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setHovered(false) } : undefined}
      >
        {arrowsWhere === 'top' && bar}
        <div ref={viewportRef} className={cls(props.viewportClass, 'overflow-hidden')}>
          <RailTag
            id={railId}
            className={`carousel-rail ${cls(props.railClass, '')}`}
            style={railStyle}
            data-per-view={perView || undefined}
            data-axis={axis === 'y' ? 'y' : undefined}
          >
            {slides}
          </RailTag>
        </div>
        {arrowsWhere === 'inside' && arrowButtons}
        {arrowsWhere !== 'top' && bar}
      </div>
    </ActionDependant>
  )
}
